// 原生壳能力的薄封装：菜单栏（任务栏）计时标题 + 系统通知。
// 在 Tauri 桌面壳里走 Rust 命令；在普通网页里优雅降级（通知用 Web Notification，标题忽略）。
import { isTauri } from "./platform";

function tauriInvoke(cmd, args) {
  try {
    const t = window.__TAURI__;
    if (t && t.core && typeof t.core.invoke === "function") {
      return t.core.invoke(cmd, args);
    }
  } catch (e) {
    /* ignore */
  }
  return Promise.resolve();
}

// 更新 mac 菜单栏里的计时文字。传空字符串则清空（回到纯图标）。
export function setTrayTitle(title) {
  if (!isTauri()) return;
  tauriInvoke("set_tray_title", { title: title || "" }).catch(() => {});
}

// 唤起原生微信 APP 支付（仅 Tauri 移动端壳，且需原生集成微信 OpenSDK）。
// 入参为后端 APP 下单返回的调起参数（appid/partnerId/prepayId/package/nonceStr/timeStamp/sign）。
// 约定 Rust 端返回 { ok: true } 表示已唤起并支付成功；桌面壳暂为 stub，会抛错 → 上层回退扫码。
export function nativeWechatPay(params) {
  if (!isTauri()) return Promise.reject(new Error("not-tauri"));
  const t = window.__TAURI__;
  if (!t || !t.core || typeof t.core.invoke !== "function") {
    return Promise.reject(new Error("tauri-invoke-unavailable"));
  }
  return t.core.invoke("wechat_pay", { params });
}

// 是否支持 Web Notification（非 Tauri 环境）。
function webNotifySupported() {
  return typeof Notification !== "undefined";
}

// 在用户手势内请求一次通知权限（如点击「开始」）。
// Web Notification 的 requestPermission 必须由手势触发，否则部分浏览器直接拒绝；
// 因此单独导出，供上层在开始专注时调用，而不是在真正要发通知的无手势上下文里补请求。
// Tauri 壳由系统统一管理权限，这里直接返回 true。
export function ensureNotifyPermission() {
  if (isTauri()) return Promise.resolve(true);
  if (!webNotifySupported()) return Promise.resolve(false);
  if (Notification.permission === "granted") return Promise.resolve(true);
  if (Notification.permission === "denied") return Promise.resolve(false);
  try {
    // 新版返回 Promise；老版用回调，这里包一层兼容。
    const r = Notification.requestPermission((p) => p);
    if (r && typeof r.then === "function") {
      return r.then((p) => p === "granted").catch(() => false);
    }
    return Promise.resolve(Notification.permission === "granted");
  } catch (e) {
    return Promise.resolve(false);
  }
}

// 发系统通知。Tauri 走原生通知中心；网页端有权限时用 Web Notification 兜底。
// Android Chrome 的页面上下文 new Notification() 会抛 TypeError（须走 Service Worker），
// 因此优先尝试已注册的 Service Worker 通知，失败再回退构造函数，两条路径都不让异常冒泡。
export function nativeNotify(title, body) {
  if (isTauri()) {
    tauriInvoke("notify", { title: title || "Zeno", body: body || "" }).catch(
      () => {}
    );
    return;
  }
  if (!webNotifySupported() || Notification.permission !== "granted") return;
  const t = title || "Zeno";
  const opts = { body: body || "", icon: "/favicon.ico" };
  // 优先 Service Worker 通知（Android 必需，且不受页面上下文限制）。
  if (
    typeof navigator !== "undefined" &&
    navigator.serviceWorker &&
    navigator.serviceWorker.controller
  ) {
    navigator.serviceWorker.ready
      .then((reg) => reg.showNotification(t, opts))
      .catch(() => fallbackNotify(t, opts));
    return;
  }
  fallbackNotify(t, opts);
}

function fallbackNotify(title, opts) {
  try {
    // eslint-disable-next-line no-new
    new Notification(title, opts);
  } catch (e) {
    /* Android 页面上下文不支持构造函数；无 SW 时静默放弃 */
  }
}
